/**
 * LoadingSpinner Component
 *
 * Displays a loading spinner using Tyler Forge circular progress indicator
 */

import '@tylertech/forge/circular-progress';
import './LoadingSpinner.css';

export interface LoadingSpinnerProps {
  /** Size of the spinner (default: 48px) */
  size?: number;
  /** Message to display below spinner */
  message?: string;
  /** Center the spinner on the page */
  centered?: boolean;
  /** ARIA label for accessibility */
  ariaLabel?: string;
}

/**
 * Loading spinner with optional message
 *
 * @example
 * ```tsx
 * <LoadingSpinner message="Loading services..." />
 * <LoadingSpinner centered />
 * ```
 */
export function LoadingSpinner({
  size = 48,
  message,
  centered = false,
  ariaLabel = 'Loading',
}: LoadingSpinnerProps): JSX.Element {
  const containerClass = centered
    ? 'loading-spinner-container centered'
    : 'loading-spinner-container';
  const ProgressElement = 'forge-circular-progress' as React.ElementType;

  return (
    <div className={containerClass}>
      <ProgressElement
        determinate={false}
        aria-label={ariaLabel}
        style={{ width: `${size}px`, height: `${size}px` } as React.CSSProperties}
      />
      {message && <p className="loading-message">{message}</p>}
    </div>
  );
}
